function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/* eslint-disable class-methods-use-this */

import { GridModel } from '@deephaven/grid';
import { EventShimCustomEvent } from '@deephaven/utils';
var EMPTY_ARRAY = [];
var EMPTY_SET = new Set();

/**
 * Abstract class that extends the GridModel to have more functionality, like filtering and sorting.
 * For use from IrisGrid.
 * Provides some abstraction from the dh.Table and dh.TreeTable classes, so we can treat them somewhat the same.
 * Note that it still uses dh.Column, dh.FilterCondition, dh.Sort, etc., still. Theoretically should abstract
 * those out as well, so there's no dependency on IrisAPI at all, but it's a lot of work for no real gain at this time.
 */
class IrisGridModel extends GridModel {
  constructor(dh) {
    super();
    _defineProperty(this, "dh", void 0);
    _defineProperty(this, "listenerCount", void 0);
    this.dh = dh;
    this.listenerCount = 0;
  }
  // Pulled directly from event-target-shim implementation signature
  // https://github.com/mysticatea/event-target-shim/blob/master/src/lib/event-target.ts#L99
  // Using Parameters<GridModel['addEventListener']> doesn't work
  addEventListener(type0, callback0, options0) {
    super.addEventListener(type0, callback0, options0);
    this.listenerCount += 1;
    if (this.listenerCount === 1) {
      this.startListening();
    }
  }
  removeEventListener(type0, callback0, options0) {
    super.removeEventListener(type0, callback0, options0);
    this.listenerCount -= 1;
    if (this.listenerCount === 0) {
      this.stopListening();
    }
  }

  /**
   * Function called when first listener is added.
   * Override for implementation specific behaviour.
   */
  startListening() {
    // no-op
  }

  /**
   * Function called when last listener is removed.
   * Override for implementation specific behaviour.
   */
  stopListening() {
    // no-op
  }

  /**
   * Dispatch a `PENDING` event to raise the IrisGrid loading scrim for an
   * async, config-changing operation initiated on this model.
   * @param detail Optional self-describing detail (scrim text and options).
   */
  dispatchPending(detail) {
    this.dispatchEvent(new EventShimCustomEvent(IrisGridModel.EVENT.PENDING, {
      detail
    }));
  }

  /**
   * Gets the columns for this model
   * @returns All columns in the model
   */

  /**
   * Retrieve the aggregated columns for this model
   * @returns The columns that are aggregated
   */
  get aggregatedColumns() {
    return EMPTY_ARRAY;
  }

  /**
   * Retrieve the grouped columns for this model
   * @returns The columns that are grouped
   */
  get groupedColumns() {
    return EMPTY_ARRAY;
  }

  /**
   * Gets the columns for the model before any transformations (such as rollups) are applied.
   * @returns All original columns in the model.
   */
  get originalColumns() {
    return this.columns;
  }

  /**
   * Gets the column index for this model
   * @param name The model column name.
   * @returns The numeric index of the requested column.
   */

  /**
   * Retrieve the source cell for a given cell. Returns something different if the cell is a proxied cell
   * that retrieves data from another cell.
   * @param column Column to get the source for
   * @param row Row to get the source for
   * @returns Source cell where the data is coming from
   */
  sourceForCell(column, row) {
    return {
      column,
      row
    };
  }

  /**
   * Retrieve the range of columns to clear filters on for a given column.
   * @param column Column to get the range of filters to clear.
   * @returns Axis range of the column filters to clear, or `null` if this should not have a clear filter option.
   */
  getClearFilterRange(column) {
    if (this.isFilterable(column)) {
      return [column, column];
    }
    return null;
  }

  /** List of column movements defined by the model. Used as initial movements for IrisGrid */
  get initialMovedColumns() {
    return EMPTY_ARRAY;
  }

  /** List of row movements defined by the model. Used as initial movements for IrisGrid */
  get initialMovedRows() {
    return EMPTY_ARRAY;
  }

  /** List of column header groups defined by the model */
  get initialColumnHeaderGroups() {
    return EMPTY_ARRAY;
  }

  /**
   * @param column The model column index
   * @param row The model row index
   * @returns The format stored for that cell
   */

  /**
   * @param column The model column index
   * @param row The model row index
   * @returns The value stored for that cell
   */

  /**
   * @returns The filters set on this model
   */

  /**
   * @param filter The filters to set
   */

  /**
   * @returns The formatter used when formatting data
   */

  /**
   * @param formatter The formatter to set
   */

  /**
   * @returns The column alignment map
   */

  /**
   * @param columnAlignmentMap The column alignment map to set
   */

  /**
   * @param value The value to format
   * @param columnType The column type to format
   * @param columnName The column name to format
   */

  /**
   * @returns The sorts used on this model
   */

  /**
   * @param sort The sorts to use on this model
   */

  /**
  /**
   * @returns The custom columns on this model
   */

  /**
   * @param customColumns The custom columns to use
   */

  /**
   * @returns The format columns on this model
   */

  /**
   * @param formatColumns The format columns to use
   */

  /**
   * @param columns The columns to treat as frozen
   */

  /**
   * @returns The config to use for rolling up this table
   */

  /**
   * @returns The config to use for the totals table of this model
   */

  /**
   * @returns The LayoutHints to use for the columns of this table model
   */
  get layoutHints() {
    return null;
  }

  /**
   * @returns Names of columns which should be locked to the front, but not floating
   */
  get frontColumns() {
    return EMPTY_ARRAY;
  }

  /**
   * @returns Names of columns which should be locked to the back, but not floating
   */
  get backColumns() {
    return EMPTY_ARRAY;
  }

  /**
   * @returns Names of key columns
   */
  get keyColumnSet() {
    return EMPTY_SET;
  }

  /**
   * @returns Names of value columns
   */
  get valueColumnSet() {
    return EMPTY_SET;
  }

  /**
   * @returns Names of columns which should be frozen to the front and floating
   */
  get frozenColumns() {
    return EMPTY_ARRAY;
  }

  /**
   * @param index The column index to check
   * @returns Whether the column is one of LayoutHints' frozen columns
   */
  isColumnFrozen(index) {
    return false;
  }

  /**
   * @param index The column index to check
   * @returns Whether the column is sortable
   */
  isColumnSortable(index) {
    return false;
  }

  /**
   * @deprecated Replaced with isPartitionRequired()
   * @returns True if this model requires a filter to be set
   */
  get isFilterRequired() {
    return false;
  }
  get isReversible() {
    return true;
  }

  /**
   * @returns Returns a raw table that is frozen and can be used for exporting data
   */

  /**
   * @returns True if this model supports the columnStatistics(column) function
   */
  get isColumnStatisticsAvailable() {
    return false;
  }

  /**
   * The description for this model.
   * @returns The description of the model
   */
  get description() {
    return '';
  }

  /**
   * @param column The column to get statistics for
   * @returns The column statistics
   */

  /**
   * @returns True if this model supports aggregatedColumns
   */
  get isAggregatedColumnsAvailable() {
    return false;
  }

  /**
   * @returns True if this model supports customColumns
   */
  get isCustomColumnsAvailable() {
    return false;
  }

  /**
   * @returns True if this model supports customColumns
   */
  get isFormatColumnsAvailable() {
    return false;
  }

  /**
   * @returns True if this model supports the export() function
   */
  get isExportAvailable() {
    return false;
  }

  /**
   * @returns True if this model supports the valuesTable(column) function
   */
  get isValuesTableAvailable() {
    return false;
  }

  /**
   * @returns True if this model should allow the chart builder
   */
  get isChartBuilderAvailable() {
    return false;
  }

  /**
   * @returns True if this model should allow to organize columns
   */
  get isOrganizeColumnsAvailable() {
    return false;
  }

  /**
   * @returns True if the rollup rows functionality is available
   */
  get isRollupAvailable() {
    return false;
  }

  /**
   * @returns True if select distinct functionality is available
   */
  get isSelectDistinctAvailable() {
    return false;
  }

  /**
   * @returns True if the totals functionality is available
   */
  get isTotalsAvailable() {
    return false;
  }

  /**
   * The names of columns with select distinct enabled
   * @returns An array of column names
   */

  /**
   * Set the columns with select distinct enabled
   * @param names The array of column names to enable select distinct on
   */

  /**
   * The pending data for this model
   * @returns A map of row index to a map of column name/value pairs
   */

  /**
   * Set the pending data for this model
   * @param A map of row index to a map of column name/value pairs
   */

  /**
   * @returns The count of pending rows to show
   */

  /**
   * Set the count of pending rows to show
   * @param count The count of pending rows to show
   */

  /**
   * Errors for the pending data
   * @returns Map from row number to the error
   */

  /**
   * Commit pending data and save all data to the table
   */

  /**
   * Check if viewport is still loading data
   */
  get isViewportPending() {
    return false;
  }

  /**
   * Check if a column is filterable
   * @param columnIndex The column index to check for filterability
   * @returns True if the current provided column index is filterable, false otherwise
   */
  isFilterable(columnIndex) {
    return false;
  }

  /**
   * Set the indices of the viewport
   * @param top Top of viewport
   * @param bottom Bottom of viewport
   * @param columns The columns in the viewport. `undefined` for all columns
   */

  /**
   * Takes a snapshot of the provided ranges
   * @param ranges The model ranges to take the snapshot of
   * @returns Returns the data in a row/column matrix
   */

  /**
   * @param ranges The ranges to take a snapshot of
   * @param includeHeaders Whether to include the headers in the snapshot or not
   * @param formatValue A function to format a value for a cell. Defaults to model format value.
   * @returns A text formatted snapshot of the data for the specified range set
   */

  /**
   * @param column The columns to get the distinct values for
   * @returns A table partitioned on the specified columns in the order given in
   */

  /**
   * Close this model. It can no longer be used after being closed
   */
  close() {
    // no-op
  }

  /**
   * Don't allow any rows to be movable in any IrisGrids by default. Just columns.
   */
  isRowMovable() {
    return false;
  }
  get isSeekRowAvailable() {
    return false;
  }
  dataBarOptionsForCell(column, row, theme) {
    throw new Error('Method not implemented.');
  }
  tooltipForCell(column, row) {
    return null;
  }
  getColumnRestrictions(column) {
    var _columnRestrictions;
    var c = this.columns[column];
    return (_columnRestrictions = c === null || c === void 0 ? void 0 : c.columnRestrictions) !== null && _columnRestrictions !== void 0 ? _columnRestrictions : [];
  }
}
_defineProperty(IrisGridModel, "EVENT", Object.freeze({
  UPDATED: 'UPDATED',
  FORMATTER_UPDATED: 'FORMATTER_UPDATED',
  REQUEST_FAILED: 'REQUEST_FAILED',
  COLUMNS_CHANGED: 'COLUMNS_CHANGED',
  TABLE_CHANGED: 'TABLE_CHANGED',
  /**
   * Fired by `IrisGridProxyModel` whenever its inner model is swapped for a
   * different instance (e.g. pivot-builder swapping a table model for a pivot
   * model, or back). The `detail` is the new inner model. Unlike
   * `TABLE_CHANGED` (only dispatched for table-template models), this fires on
   * every inner-model swap regardless of model class, so the host can reset
   * view state (e.g. `movedColumns`) that the previous model owned.
   */
  SCHEMA_CHANGED: 'SCHEMA_CHANGED',
  FILTERS_CHANGED: 'FILTERS_CHANGED',
  SORTS_CHANGED: 'SORTS_CHANGED',
  DISCONNECT: 'DISCONNECT',
  RECONNECT: 'RECONNECT',
  TOTALS_UPDATED: 'TOTALS_UPDATED',
  /** Fired when the viewport is applied to the table and we're waiting for a response. */
  PENDING_DATA_UPDATED: 'PENDING_DATA_UPDATED',
  VIEWPORT_UPDATED: 'VIEWPORT_UPDATED',
  /**
   * Fired when an async, config-changing operation has begun. Raises the
   * IrisGrid loading scrim. Detail is an optional, self-describing
   * {@link PendingOperationDetail} so an initiator (e.g. a plugin) can raise
   * the scrim for an operation IrisGrid has no built-in knowledge of.
   */
  PENDING: 'PENDING',
  /**
   * Optional completion signal for a pending operation that does NOT
   * naturally end in `UPDATED` / `COLUMNS_CHANGED` / `REQUEST_FAILED`.
   * Built-in operations never need this; their existing events clear the
   * scrim.
   *
   * Assumes a single outstanding model-driven operation: the scrim is not
   * ref-counted, so a `PENDING_CLEARED` clears it regardless of other
   * in-flight operations. Ref-counting is deferred to the planned migration
   * that routes built-in `startLoading` calls through these events, where a
   * depth counter can stay balanced.
   */
  PENDING_CLEARED: 'PENDING_CLEARED'
}));
export var STRING_LIST_RESTRICTION_TYPE = 'io.deephaven.proto.backplane.grpc.StringListRestriction';

/**
 * Restriction type for columns whose values must come from a predefined list of strings.
 */

export default IrisGridModel;
//# sourceMappingURL=IrisGridModel.js.map